import { Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export interface EditableSelectOption<T extends string> {
  label: string;
  value: T;
}

interface EditableSelectProps<T extends string> {
  value: T;
  options: EditableSelectOption<T>[];
  onSave: (value: T) => Promise<void> | void;
  onCancel?: () => void;
  isEditing: boolean;
  onStartEdit: () => void;
}

export function EditableSelect<T extends string>({ 
  value,
  options,
  onSave,
  onCancel,
  isEditing,
  onStartEdit
}: EditableSelectProps<T>) {
  if (isEditing) {
    return (
      <div className="flex items-center gap-2">
        <Select
          value={value}
          onValueChange={(newValue) => {
            onSave(newValue as T);
          }}
        >
          <SelectTrigger className="h-8">
            <SelectValue placeholder="Select a value" />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {onCancel && (
          <Button variant="ghost" size="icon" onClick={onCancel}>
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
    );
  }

  return (
    <div
      className="cursor-pointer hover:bg-accent hover:text-accent-foreground p-2 rounded"
      onClick={onStartEdit}
    >
      {options.find(opt => opt.value === value)?.label || value}
    </div>
  );
}